import React, { useState } from 'react';
import { X, Upload, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { DateTimePicker } from './DateTimePicker';
import { CARE_TYPES } from '../constants';
import { CreatePostFormData } from '../types';
import {
  validatePostTitle,
  validatePostDescription,
  validateBudget,
  validateUKPostcode,
  validateFutureDate,
  validateImageFile
} from '../utils/validation';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: CreatePostFormData) => Promise<void>;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<CreatePostFormData>({
    title: '',
    description: '',
    careType: '',
    city: '',
    postalCode: '',
    appointmentDate: '',
    budget: 0,
    photo: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const handleChange = (field: keyof CreatePostFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, photo: validation.message || '' }));
      return;
    }

    setPhotoFile(file);
    setFormData(prev => ({ ...prev, photo: file }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    if (errors.photo) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.photo;
        return newErrors;
      });
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, photo: null }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const titleValidation = validatePostTitle(formData.title);
    if (!titleValidation.isValid) {
      newErrors.title = titleValidation.message || '';
    }

    const descriptionValidation = validatePostDescription(formData.description);
    if (!descriptionValidation.isValid) {
      newErrors.description = descriptionValidation.message || '';
    }

    if (!formData.careType) {
      newErrors.careType = 'Care type is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!validateUKPostcode(formData.postalCode)) {
      newErrors.postalCode = 'Please enter a valid UK postcode';
    }

    const dateValidation = validateFutureDate(formData.appointmentDate);
    if (!dateValidation.isValid) {
      newErrors.appointmentDate = dateValidation.message || '';
    }

    const budgetValidation = validateBudget(formData.budget);
    if (!budgetValidation.isValid) {
      newErrors.budget = budgetValidation.message || '';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error submitting post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      careType: '',
      city: '',
      postalCode: '',
      appointmentDate: '',
      budget: 0,
      photo: null,
    });
    setErrors({});
    setPhotoPreview(null);
    setPhotoFile(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Care Request Post">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="e.g., Need dementia care for elderly mother"
            className={errors.title ? 'border-red-500' : ''}
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.title}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">{formData.title.length}/100 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Provide detailed information about the care required..."
            rows={6}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.description}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">{formData.description.length}/1000 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Care Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.careType}
            onChange={(e) => handleChange('careType', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
              errors.careType ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select care type</option>
            {CARE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.careType && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.careType}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="e.g., London"
              className={errors.city ? 'border-red-500' : ''}
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.city}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.postalCode}
              onChange={(e) => handleChange('postalCode', e.target.value.toUpperCase())}
              placeholder="e.g., NW1 6XE"
              className={errors.postalCode ? 'border-red-500' : ''}
            />
            {errors.postalCode && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.postalCode}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Appointment Date <span className="text-red-500">*</span>
          </label>
          <DateTimePicker
            value={formData.appointmentDate}
            onChange={(value) => handleChange('appointmentDate', value)}
            error={errors.appointmentDate}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Budget (£) <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={formData.budget || ''}
            onChange={(e) => handleChange('budget', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 200"
            min="20"
            max="10000"
            className={errors.budget ? 'border-red-500' : ''}
          />
          {errors.budget && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.budget}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">Enter your budget range for this care service</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo (Optional)
          </label>

          {!photoPreview ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotoChange}
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to upload photo</span>
                  <span className="text-xs text-gray-500">JPG, PNG or WebP (Max 5MB)</span>
                </div>
              </label>
            </div>
          ) : (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={removePhoto}
                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
              >
                <X className="w-4 h-4" />
              </button>
              {photoFile && (
                <p className="mt-2 text-sm text-gray-600">
                  {photoFile.name} ({(photoFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          )}

          {errors.photo && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.photo}
            </p>
          )}
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? 'Creating Post...' : 'Create Post'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreatePostModal;
